import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { ReduxStore } from './payload-types';
import { Dispatch } from 'redux';
import { get, has } from 'lodash';
import { API_TIME_OUT, AUTH_HEADERS, HTTP_STATUS_CODES, SUPPORTED_LOCALES, Language, COVERME_REFERENCE_ID } from '@trip-ux/constants';
import { getRefCode } from './api-utils';

const GATEWAY_TIMEOUT = 504;
const REQUEST_TIMEOUT = 408;
const CSRF_RETRY_HEADER = 'x-csrf-retry';

/**
 * Re-fetch CSRF token from the BFF and retry the original request once.
 * Returns the retried response data on success, or undefined if re-fetch fails.
 */
const retryCsrfRequest = async (
  error: AxiosError
): Promise<unknown | undefined> => {
  const originalRequest = error.config as AxiosRequestConfig & { [CSRF_RETRY_HEADER]?: boolean };
  if (!originalRequest || originalRequest[CSRF_RETRY_HEADER]) {
    return undefined;
  }

  originalRequest[CSRF_RETRY_HEADER] = true;
  try {
    const csrfResponse = await axios.get('/csrf', { withCredentials: true });
    const newToken = csrfResponse.data?.csrfToken;
    if (newToken) {
      axios.defaults.headers.common['x-csrf-token'] = newToken;
      originalRequest.headers = {
        ...originalRequest.headers,
        'x-csrf-token': newToken
      };
      const retryResponse = await axios(originalRequest);
      return retryResponse.data;
    }
  } catch {
    // CSRF re-fetch failed — caller falls through to handleForbidden
  }
  return undefined;
};

/**
 * Enrich axios errors with `isTimeout` flag for consistent upstream handling.
 * Covers client-side timeouts (ECONNABORTED) and server gateway timeouts (504/408).
 */
/** @internal Exported for testing only */
export const enrichTimeoutError = (error: AxiosError): AxiosError => {
  const status = error.response?.status;
  const isTimeout =
    error.code === 'ECONNABORTED' ||
    status === GATEWAY_TIMEOUT ||
    status === REQUEST_TIMEOUT;
  (error as AxiosError & { isTimeout: boolean }).isTimeout = isTimeout;
  return error;
};

/**
 * Handle 403 Forbidden by redirecting to home page.
 * This covers CSRF token expiry (EBADCSRFTOKEN) and B2C session loss
 * (empty session → "Lost session!" or empty 403 from IdleController).
 * A single redirect flag prevents infinite reload loops.
 *
 * Excluded: "More than one weblink" 403 — this is a user error (multiple tabs),
 * not session loss, and should be displayed to the user rather than triggering a redirect.
 */
/** @internal Exported for testing only */
export let forbiddenRedirectPending = false;
/** @internal Exported for testing only */
export const resetForbiddenRedirect = (): void => { forbiddenRedirectPending = false; };

const isSessionLoss403 = (status: number | undefined, data: Record<string, unknown> | undefined): boolean => {
  if (status !== HTTP_STATUS_CODES.FORBIDDEN) {
    return false;
  }
  if (data?.code === 'EBADCSRFTOKEN') {
    return true;
  }
  if (data?.message === 'Lost session!') {
    return true;
  }
  if (!data || Object.keys(data).length === 0) {
    return true;
  }
  return false;
};

/** @internal Exported for testing only */
export const handleForbidden = (error: AxiosError): void => {
  const data = error.response?.data as Record<string, unknown> | undefined;
  if (isSessionLoss403(error.response?.status, data) && !forbiddenRedirectPending) {
    forbiddenRedirectPending = true;
    window.location.href = '/';
  }
};

/**
 * Handle 401 Unauthorized by redirecting to login page.
 * This occurs when session expires. A single redirect flag prevents
 * multiple concurrent API failures from triggering multiple login flows.
 */
/** @internal Exported for testing only */
export let unauthorizedRedirectPending = false;
/** @internal Exported for testing only */
export const resetUnauthorizedRedirect = (): void => { unauthorizedRedirectPending = false; };
/** @internal Exported for testing only */
export const handleUnauthorized = (error: AxiosError): void => {
  const data = error.response?.data as Record<string, unknown> | undefined;
  if (
    error.response?.status === HTTP_STATUS_CODES.UNAUTHORIZED &&
    (data?.code === 'SESSION_EXPIRED' || data?.code === 'NOT_AUTHENTICATED') &&
    !unauthorizedRedirectPending
  ) {
    unauthorizedRedirectPending = true;
    // Always redirect to root - do not use server-provided URL to prevent open redirect
    window.location.href = '/';
  }
};

export const getQueryParams = (): Record<string, string> => {
  const params: Record<string, string> = {};
  const queryString = window.location.search.substring(1);
  const queryArray = queryString.split('&');

  queryArray.forEach(param => {
    const [key, value] = param.split('=');
    if (key) {
      params[decodeURIComponent(key)] = decodeURIComponent(value || '');
    }
  });
  return params;
};

export const prepareHeaders = (state: ReduxStore) => ({
  [AUTH_HEADERS.USER_REF]: get(state, 'userProfile.userRef'), //not needed
  [AUTH_HEADERS.USER_TYPE]: get(state, 'userProfile.userType'),
  [AUTH_HEADERS.USER_CLAIMS]: get(state, 'userProfile.accessToken'),
  'url-ref-code': getRefCode(state, getQueryParams()),
  ...(has(state, 'userProfile.scope') && { [AUTH_HEADERS.SCOPE]: get(state, 'userProfile.scope') })
});

export interface ApiClient {
  (dispatch: Dispatch, getState: () => ReduxStore): AxiosInstance;
}
export const apiClient: ApiClient = (dispatch: Dispatch, getState) => {
  const state = getState();
  const queryParams = getQueryParams(); // Get query string parameters
  const client = axios.create({
    timeout: API_TIME_OUT,
    baseURL: get(state, 'config.options.contextPath', ''),
    headers: {
      lang: state.config.locale === SUPPORTED_LOCALES.ENGLISH ? Language.EN : Language.FR,
      'url-ref-code': getRefCode(state, queryParams)
    }
  });
  client.interceptors.response.use(
    (response) => {
      if (response.config.responseType === 'arraybuffer') {
        return response
      }
      return response.data;
    },
    async (error: AxiosError) => {
      enrichTimeoutError(error);

      const data = error.response?.data as Record<string, unknown> | undefined;
      if (
        error.response?.status === HTTP_STATUS_CODES.FORBIDDEN &&
        data?.code === 'EBADCSRFTOKEN'
      ) {
        const retryResult = await retryCsrfRequest(error);
        if (retryResult !== undefined) {
          return retryResult;
        }
      }

      handleForbidden(error);
      handleUnauthorized(error);
      return Promise.reject(error);
    }
  );
  return client;
};

export const apiClientForDownload: ApiClient = (dispatch: Dispatch, getState) => {
  const state = getState();
  const queryParams = getQueryParams(); // Get query string parameters
  const client = axios.create({
    timeout: API_TIME_OUT,
    baseURL: get(state, 'config.options.contextPath', ''),
    headers: {
      'url-ref-code': getRefCode(state, queryParams)
    }
  });
  client.interceptors.response.use(
    res => {
      return res;
    },
    async (error: AxiosError) => {
      enrichTimeoutError(error);

      const data = error.response?.data as Record<string, unknown> | undefined;
      if (
        error.response?.status === HTTP_STATUS_CODES.FORBIDDEN &&
        data?.code === 'EBADCSRFTOKEN'
      ) {
        const retryResult = await retryCsrfRequest(error);
        if (retryResult !== undefined) {
          return retryResult;
        }
      }

      handleForbidden(error);
      return Promise.reject(error);
    }
  );
  return client;
};
